import { HTMLAttributes, InputHTMLAttributes, ReactNode, RefObject } from 'react'
import {
	TextField,
	Label,
	Input as AriaInput,
	TextFieldProps,
	InputProps as AriaInputProps,
} from 'react-aria-components'

type InputProps = Omit<TextFieldProps, 'className'> & {
	className?: string
	innerRef?: RefObject<HTMLInputElement>
	label?: ReactNode
	inputProps?: AriaInputProps
	min?: InputHTMLAttributes<HTMLInputElement>['min']
}

const inputClass =
	'bg-transparent text-sm text-gray-700 font-sans font-normal outline outline-0 disabled:border-gray-100 border border-gray-400 rounded focus:border-gray-900'
const readOnlyClass =
	'bg-gray-200 text-sm text-gray-700 font-sans font-normal outline outline-0 border border-gray-400 rounded'
const colorInputClass = 'px-1'
const notColorInputClass = 'w-full p-2'

export function Input({ innerRef, label, className, inputProps, min, ...props }: InputProps) {
	if (props.isReadOnly) {
		return (
			<div className={`flex flex-col gap-1 ${className ?? ''}`}>
				{label && (
					<span className="flex gap-1 font-normal leading-tight text-xs">{label}</span>
				)}
				<div className={`${readOnlyClass} ${notColorInputClass}`}>
					{props.value ?? props.defaultValue ?? '​'}
				</div>
			</div>
		)
	}
	return (
		<TextField className={`flex flex-col gap-1 ${className ?? ''}`} {...props}>
			{label && (
				<Label className="flex gap-1 font-normal leading-tight text-xs">{label}</Label>
			)}
			<AriaInput
				ref={innerRef}
				className={`${inputClass} ${props.type === 'color' ? colorInputClass : notColorInputClass} invalid:border-red-600`}
				min={min}
				{...inputProps}
			/>
		</TextField>
	)
}

type FileInputProps = Omit<TextFieldProps, 'className'> & {
	className?: string
	innerRef?: RefObject<HTMLInputElement>
	label?: ReactNode
	inputProps?: HTMLAttributes<HTMLInputElement>
}

export function FileInput({
	innerRef,
	label,
	className,
	inputProps,
	isReadOnly,
	...props
}: FileInputProps) {
	if (isReadOnly) {
		return <Input isReadOnly {...props} />
	}
	return (
		<TextField className={`flex flex-col gap-1 ${className ?? ''}`} {...props}>
			{label && (
				<Label className="flex gap-1 font-normal leading-tight text-xs">{label}</Label>
			)}
			<input
				ref={innerRef}
				type="file"
				name={props.name}
				className={`${inputClass} ${notColorInputClass}`}
				{...inputProps}
			/>
		</TextField>
	)
}
